import { Asset } from "expo-asset";
import * as ImagePicker from "expo-image-picker";
import { useState } from "react";
import {
    StyleSheet,
    View,
    TouchableOpacity,
    ActivityIndicator,
    Modal,
    ScrollView
} from "react-native";
import { Text, Icon } from "react-native-paper";

import { VerificationStatus } from "./VerificationStatus";

import { submitUpload, confirmUpload, type ObItemWithSubmission } from "@/lib/onboarding-api";
import { colors } from "@/lib/theme";
import { uploadFromUri } from "@/lib/upload-from-uri";

interface PhotoUploadSectionProps {
    rnId: string;
    items: ObItemWithSubmission[];
    onUploadComplete?: () => void;
}

// Dev mode test documents — each maps to a PNG in assets/test-docs/
const TEST_DOCS = [
    // --- TB Tests ---
    { label: "✅ TB QuantiFERON (Negative)", file: "tb-quantiferon-negative.png" },
    { label: "⚠️ TB QuantiFERON (Positive)", file: "tb-quantiferon-positive.png" },
    { label: "❌ TB QuantiFERON (Indeterminate)", file: "tb-quantiferon-indeterminate.png" },
    { label: "⚠️ TB PPD/Skin Test (Negative)", file: "tb-ppd-negative.png" },
    { label: "⚠️ TB PPD (Positive)", file: "tb-ppd-positive.png" },
    // --- Physical Exam ---
    { label: "✅ Physical Exam (Valid)", file: "physical-valid.png" },
    { label: "❌ Physical Exam (Expired)", file: "physical-expired.png" },
    { label: "❌ Physical Exam (No Signature)", file: "physical-no-signature.png" },
    // --- MMR (Measles / Rubella) ---
    { label: "✅ MMR Titer — Both Immune", file: "mmr-titer-both-immune.png" },
    { label: "✅ MMR Vaccination — 2 Doses", file: "mmr-vaccination-2-doses.png" },
    { label: "⚠️ MMR Vaccination — 1 Dose Only", file: "mmr-vaccination-1-dose.png" },
    { label: "⚠️ MMR — Measles Equivocal", file: "mmr-measles-equivocal.png" },
    // --- Follow-ups ---
    { label: "✅ PPD Step 2 (Negative)", file: "ppd2-negative.png" },
    { label: "⚠️ PPD Step 2 (Positive)", file: "ppd2-positive.png" },
    { label: "✅ Chest X-Ray (Normal)", file: "chest-xray-normal.png" },
    { label: "❌ Chest X-Ray (Abnormal)", file: "chest-xray-abnormal.png" },
    // --- Edge cases ---
    { label: "❌ Wrong Document (Phone Bill)", file: "wrong-document.png" },
    { label: "❌ Blurry/Unreadable Document", file: "blurry-document.png" }
];

// Map filenames to require() calls for Expo asset resolution
/* eslint-disable @typescript-eslint/no-require-imports */
const TEST_DOC_ASSETS: Record<string, number> = {
    "tb-quantiferon-negative.png": require("@/assets/test-docs/tb-quantiferon-negative.png"),
    "tb-quantiferon-positive.png": require("@/assets/test-docs/tb-quantiferon-positive.png"),
    "tb-quantiferon-indeterminate.png": require("@/assets/test-docs/tb-quantiferon-indeterminate.png"),
    "tb-ppd-negative.png": require("@/assets/test-docs/tb-ppd-negative.png"),
    "tb-ppd-positive.png": require("@/assets/test-docs/tb-ppd-positive.png"),
    "physical-valid.png": require("@/assets/test-docs/physical-valid.png"),
    "physical-expired.png": require("@/assets/test-docs/physical-expired.png"),
    "physical-no-signature.png": require("@/assets/test-docs/physical-no-signature.png"),
    "mmr-titer-both-immune.png": require("@/assets/test-docs/mmr-titer-both-immune.png"),
    "mmr-vaccination-2-doses.png": require("@/assets/test-docs/mmr-vaccination-2-doses.png"),
    "mmr-vaccination-1-dose.png": require("@/assets/test-docs/mmr-vaccination-1-dose.png"),
    "mmr-measles-equivocal.png": require("@/assets/test-docs/mmr-measles-equivocal.png"),
    "ppd2-negative.png": require("@/assets/test-docs/ppd2-negative.png"),
    "ppd2-positive.png": require("@/assets/test-docs/ppd2-positive.png"),
    "chest-xray-normal.png": require("@/assets/test-docs/chest-xray-normal.png"),
    "chest-xray-abnormal.png": require("@/assets/test-docs/chest-xray-abnormal.png"),
    "wrong-document.png": require("@/assets/test-docs/wrong-document.png"),
    "blurry-document.png": require("@/assets/test-docs/blurry-document.png")
};
/* eslint-enable @typescript-eslint/no-require-imports */

// Test/sample-doc injector disabled — always use the real capture/upload flow.
const DEV_MODE = false;

function getApprovedNote(extraction: Record<string, string>): string {
    // TB
    if (extraction.tb_outcome === "quantiferon_negative") {
        return "QuantiFERON — negative";
    }
    if (extraction.ppd2_outcome === "negative") {
        return "PPD2 — negative · TB cleared";
    }
    if (extraction.cxr_outcome === "normal") {
        return "Chest X-ray — normal · TB cleared";
    }
    // MMR (combined)
    if (extraction.measles_verdict && extraction.rubella_verdict) {
        const m = extraction.measles_verdict === "cleared" ? "Measles ✓" : "Measles pending";
        const r = extraction.rubella_verdict === "cleared" ? "Rubella ✓" : "Rubella pending";
        if (extraction.measles_verdict === "cleared" && extraction.rubella_verdict === "cleared") {
            return "Measles ✓ · Rubella ✓";
        }
        return `${m} · ${r}`;
    }
    // MMR follow-ups
    if (extraction.mmr2_verdict === "cleared") {
        return "MMR Dose 2 — complete";
    }
    if (extraction.measles_repeat_verdict === "cleared") {
        return "Repeat titer — Immune";
    }
    if (extraction.rubella_repeat_verdict === "cleared") {
        return "Repeat titer — Immune";
    }
    // Physical
    if (extraction.physical_outcome === "cleared") {
        return "Cleared for duty";
    }
    return "";
}

/**
 * Renders a checklist of document_upload items with a single "Upload Document" button.
 * In dev mode, shows a picker of test documents instead of the camera.
 */
export function PhotoUploadSection({ rnId, items, onUploadComplete }: PhotoUploadSectionProps) {
    const [uploading, setUploading] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showDevPicker, setShowDevPicker] = useState(false);

    async function uploadFile(uri: string, filename: string, contentType: string) {
        const { upload, uploadUrl } = await submitUpload(rnId, {
            filename,
            contentType
        });

        await uploadFromUri({ uri, uploadUrl, contentType });

        await confirmUpload(rnId, upload.id);
    }

    async function handleCameraUpload() {
        setUploading(true);
        setError(null);

        try {
            const result = await ImagePicker.launchCameraAsync({
                mediaTypes: ["images"],
                quality: 0.7
            });

            if (result.canceled) {
                setUploading(false);
                return;
            }

            const asset = result.assets[0];
            const uriPath = asset.uri.startsWith("blob:") ? "" : asset.uri;
            const rawExt = uriPath.split(".").pop() ?? "";
            const ext = /^[a-zA-Z0-9]{1,5}$/.test(rawExt) ? rawExt : "jpg";

            await uploadFile(asset.uri, `document_${Date.now()}.${ext}`, `image/${ext}`);
            onUploadComplete?.();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Upload failed");
        } finally {
            setUploading(false);
        }
    }

    async function handleDevDocSelect(doc: (typeof TEST_DOCS)[0]) {
        setShowDevPicker(false);
        setUploading(true);
        setUploadingDoc(doc.label);
        setError(null);

        try {
            const assetModule = TEST_DOC_ASSETS[doc.file];
            const [loadedAsset] = await Asset.loadAsync(assetModule);
            const uri = loadedAsset.localUri ?? loadedAsset.uri;

            await uploadFile(uri, doc.file, "image/png");
            onUploadComplete?.();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Upload failed");
        } finally {
            setUploading(false);
            setUploadingDoc(null);
        }
    }

    // Index items by ID for quick lookup
    const itemsById = new Map(items.map((i) => [i.id, i]));

    return (
        <View style={styles.container}>
            {/* Checklist of items */}
            {items.map((item) => {
                const status = item.submission?.status ?? "required";
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const extraction = (item.submission as any)?.extraction as Record<
                    string,
                    string
                > | null;
                const isApproved = status === "approved";
                const isRejected = status === "rejected";
                const isVerifying = status === "ai_verifying" || status === "submitted";

                // Generic partial state: approved item has a follow-up chain that matched,
                // and the target item is still pending (not approved / not_required)
                let isPartial = false;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const chains = (item as any).followupChains as
                    | Array<{ sourceField: string; sourceValue: string; targetItemId: string }>
                    | undefined;
                if (isApproved && extraction && chains?.length) {
                    isPartial = chains.some((chain) => {
                        const extractedValue = extraction[chain.sourceField];
                        if (extractedValue?.toLowerCase() === chain.sourceValue.toLowerCase()) {
                            const target = itemsById.get(chain.targetItemId);
                            const targetStatus = target?.submission?.status;
                            return (
                                targetStatus &&
                                targetStatus !== "approved" &&
                                targetStatus !== "not_required"
                            );
                        }
                        return false;
                    });
                }

                return (
                    <View key={item.id} style={styles.itemRow}>
                        <View
                            style={[
                                styles.itemCheck,
                                isApproved && !isPartial && styles.itemCheckApproved,
                                isPartial && styles.itemCheckPartial,
                                isRejected && styles.itemCheckRejected
                            ]}
                        >
                            {isApproved && !isPartial && (
                                <Icon source="check" size={14} color="#FFFFFF" />
                            )}
                            {isPartial && <Icon source="clock-outline" size={14} color="#FFFFFF" />}
                            {isRejected && <Icon source="close" size={14} color="#FFFFFF" />}
                            {isVerifying && <ActivityIndicator size={12} color={colors.warning} />}
                        </View>
                        <View style={styles.itemInfo}>
                            <Text
                                style={[
                                    styles.itemName,
                                    isApproved && !isPartial && styles.itemNameApproved
                                ]}
                            >
                                {item.name}
                            </Text>
                            {item.description &&
                                status !== "approved" &&
                                !/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(item.description) && (
                                    <Text style={styles.itemRequirement}>{item.description}</Text>
                                )}
                            {isPartial && chains && (
                                <Text style={styles.itemPartialNote}>
                                    {chains
                                        .filter((c) => {
                                            const target = itemsById.get(c.targetItemId);
                                            const ts = target?.submission?.status;
                                            return (
                                                extraction?.[c.sourceField]?.toLowerCase() ===
                                                    c.sourceValue.toLowerCase() &&
                                                ts &&
                                                ts !== "approved" &&
                                                ts !== "not_required"
                                            );
                                        })
                                        .map(
                                            (c) =>
                                                itemsById.get(c.targetItemId)?.name ?? "follow-up"
                                        )
                                        .map((name) => `${name} still required`)
                                        .join(" · ")}
                                </Text>
                            )}
                            {isApproved && !isPartial && extraction && (
                                <Text style={styles.itemResult}>{getApprovedNote(extraction)}</Text>
                            )}
                            {isRejected && item.submission?.rejectionReason && (
                                <Text style={styles.itemRejection}>
                                    {item.submission.rejectionReason}
                                </Text>
                            )}
                        </View>
                        <VerificationStatus status={status} />
                    </View>
                );
            })}

            {/* Upload button */}
            {uploading ? (
                <View style={styles.uploadingRow}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text style={styles.uploadingText}>
                        {uploadingDoc
                            ? `Uploading: ${uploadingDoc}...`
                            : "Uploading & classifying..."}
                    </Text>
                </View>
            ) : (
                <View style={styles.buttonRow}>
                    {DEV_MODE && (
                        <TouchableOpacity
                            style={[styles.uploadButton, styles.devButton]}
                            onPress={() => setShowDevPicker(true)}
                        >
                            <Icon source="test-tube" size={20} color="#FFFFFF" />
                            <Text style={styles.uploadButtonText}>Test Docs</Text>
                        </TouchableOpacity>
                    )}
                    <TouchableOpacity
                        style={[styles.uploadButton, DEV_MODE && { flex: 1 }]}
                        onPress={handleCameraUpload}
                    >
                        <Icon source="camera" size={20} color="#FFFFFF" />
                        <Text style={styles.uploadButtonText}>Upload</Text>
                    </TouchableOpacity>
                </View>
            )}

            {error && <Text style={styles.errorText}>{error}</Text>}

            {/* Dev picker modal */}
            {DEV_MODE && (
                <Modal visible={showDevPicker} transparent animationType="slide">
                    <View style={styles.modalOverlay}>
                        <View style={styles.modalContent}>
                            <View style={styles.modalHeader}>
                                <Text style={styles.modalTitle}>Select Test Document</Text>
                                <TouchableOpacity onPress={() => setShowDevPicker(false)}>
                                    <Icon source="close" size={24} color={colors.textPrimary} />
                                </TouchableOpacity>
                            </View>
                            <ScrollView style={styles.modalScroll}>
                                {TEST_DOCS.map((doc) => (
                                    <TouchableOpacity
                                        key={doc.file}
                                        style={styles.docItem}
                                        onPress={() => handleDevDocSelect(doc)}
                                    >
                                        <Icon
                                            source="file-document-outline"
                                            size={20}
                                            color={colors.primary}
                                        />
                                        <Text style={styles.docItemText}>{doc.label}</Text>
                                    </TouchableOpacity>
                                ))}
                            </ScrollView>
                        </View>
                    </View>
                </Modal>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: 10
    },
    itemRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 8,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.surfaceBorder
    },
    itemCheck: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 2,
        borderColor: colors.surfaceBorder,
        alignItems: "center",
        justifyContent: "center"
    },
    itemCheckApproved: {
        backgroundColor: colors.success,
        borderColor: colors.success
    },
    itemCheckPartial: {
        backgroundColor: colors.warning,
        borderColor: colors.warning
    },
    itemCheckRejected: {
        backgroundColor: colors.error,
        borderColor: colors.error
    },
    itemInfo: {
        flex: 1
    },
    itemName: {
        fontSize: 14,
        fontWeight: "500",
        color: colors.textPrimary
    },
    itemRequirement: {
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 3,
        lineHeight: 16
    },
    itemNameApproved: {
        textDecorationLine: "line-through",
        color: colors.textSecondary
    },
    itemResult: {
        fontSize: 12,
        color: colors.success,
        marginTop: 2
    },
    itemPartialNote: {
        fontSize: 12,
        color: colors.warning,
        marginTop: 2
    },
    itemRejection: {
        fontSize: 12,
        color: colors.error,
        marginTop: 2
    },
    buttonRow: {
        flexDirection: "row",
        gap: 8,
        marginTop: 8
    },
    uploadButton: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        backgroundColor: colors.primary,
        borderRadius: 10,
        paddingVertical: 14
    },
    devButton: {
        backgroundColor: "#7C3AED"
    },
    uploadButtonText: {
        fontSize: 14,
        fontWeight: "700",
        color: "#FFFFFF"
    },
    uploadingRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 14
    },
    uploadingText: {
        fontSize: 13,
        color: colors.textSecondary
    },
    errorText: {
        fontSize: 12,
        color: colors.error,
        textAlign: "center"
    },
    // Dev picker modal
    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.5)",
        justifyContent: "flex-end"
    },
    modalContent: {
        backgroundColor: "#FFFFFF",
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        maxHeight: "70%",
        paddingBottom: 30
    },
    modalHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 16,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.surfaceBorder
    },
    modalTitle: {
        fontSize: 16,
        fontWeight: "700",
        color: colors.textPrimary
    },
    modalScroll: {
        paddingHorizontal: 16
    },
    docItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingVertical: 14,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.surfaceBorder
    },
    docItemText: {
        fontSize: 15,
        color: colors.textPrimary
    }
});
